import { Trans, useLingui } from '@lingui/react/macro'
import { Button, Stack, Text } from '@mantine/core'
import { IconCalendar, IconCheck } from '@tabler/icons-react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

interface SuccessStepProps {
  selectedDate: Date
  selectedTime?: Date
  estimatedEndTime?: Date
  onClose: () => void
}

export function SuccessStep({
  selectedDate,
  selectedTime,
  estimatedEndTime,
  onClose,
}: SuccessStepProps) {
  const { t } = useLingui()

  const formattedDateTime = selectedTime
    ? dayjs(selectedTime).format('dddd, D. MMMM YYYY [klo] HH:mm')
    : dayjs(selectedDate).format('dddd, D. MMMM YYYY')

  const formattedEndTime = estimatedEndTime
    ? dayjs(estimatedEndTime).format('HH:mm')
    : null

  const handleAddToCalendar = () => {
    const start = selectedTime || selectedDate
    const end = estimatedEndTime || dayjs(start).add(1.5, 'hours').toDate()

    const formatUTCDate = (date: Date) =>
      dayjs(date).utc().format('YYYYMMDDTHHmmss') + 'Z'

    const uid = `${Date.now()}-${Math.random().toString(36).substring(7)}@markku.fi`

    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Markku//Booking Widget//FI
BEGIN:VEVENT
UID:${uid}
DTSTAMP:${formatUTCDate(new Date())}
DTSTART:${formatUTCDate(start)}
DTEND:${formatUTCDate(end)}
SUMMARY:${t`Survey visit - Markku.fi`}
DESCRIPTION:${t`Markku.fi survey visit`}
END:VEVENT
END:VCALENDAR`

    const blob = new Blob([icsContent], { type: 'text/calendar' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'survey-visit.ics'
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Stack gap="md" align="center">
      <div
        style={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          backgroundColor: 'var(--mantine-color-green-1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <IconCheck size={48} color="var(--mantine-color-green-6)" />
      </div>

      <div style={{ textAlign: 'center' }}>
        <Text size="xl" fw={600} mb="xs">
          <Trans>Booking successful!</Trans>
        </Text>
        <Text>
          <Trans>Your free survey visit has been booked</Trans>
        </Text>
      </div>

      <div style={{ textAlign: 'center' }}>
        <Text fw={500}>{formattedDateTime}</Text>
        {formattedEndTime && (
          <Text size="sm" c="dimmed" mt={4}>
            <Trans>Estimated end time at {formattedEndTime}</Trans>
          </Text>
        )}
      </div>

      <Text ta="center" mt="xs">
        <Trans>We will contact you before the visit.</Trans>
      </Text>

      <Button
        onClick={handleAddToCalendar}
        size="md"
        fullWidth
        mt="md"
        leftSection={<IconCalendar size={18} />}
        variant="light"
      >
        <Trans>Add to calendar</Trans>
      </Button>

      <Button onClick={onClose} size="md" fullWidth variant="subtle">
        <Trans>Close</Trans>
      </Button>
    </Stack>
  )
}
